import { FC, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import { Box, Chip, IconButton, Paper, Typography } from '@mui/material'

import { LessonDto, LessonType } from '../../service/types'
import { getInstructorNames, NEUTRAL_COLOR } from './utils'

type Props = {
  lesson: LessonDto
  onOpenInDayPlan: (date: string, lessonId: string) => void
  children?: ReactNode
}

export const LessonCard: FC<Props> = ({ lesson, onOpenInDayPlan, children }) => {
  const { t } = useTranslation()
  const color = lesson.product?.color ?? NEUTRAL_COLOR

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1,
        backgroundColor: color + '30',
        borderLeft: `4px solid ${color}`,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
          {lesson.product?.name ?? ''}
        </Typography>
        <IconButton
          size="small"
          onClick={() => onOpenInDayPlan(lesson.date, lesson.id)}
          title={t('customers.lessons.openInDayPlan')}
        >
          <CalendarTodayIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
        <Chip size="small" label={t(`lessonType.${lesson.lessonType === LessonType.Group ? 'group' : 'private'}`)} />
        <Chip
          size="small"
          label={t(`lessonStatus.${lesson.status}`)}
          color={lesson.status === 'cancelled' ? 'error' : 'default'}
        />
        <Chip
          size="small"
          label={t(`paymentStatus.${lesson.paymentStatus}`)}
          color={
            lesson.paymentStatus === 'paid'
              ? 'success'
              : lesson.paymentStatus === 'partially_paid'
                ? 'warning'
                : 'default'
          }
        />
      </Box>
      <Typography variant="body2" color="text.secondary">
        {lesson.startTime}
        {'\u2013'}
        {lesson.endTime}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {getInstructorNames(lesson)}
      </Typography>
      {children}
    </Paper>
  )
}
